import { AppType } from "client/Api/Apps/common";
import {
	ReactElement,
	useCallback
} from "react";
import { useRenderManager } from "../../useRenderManager";
import { OneByOneTileContent } from "../OneByOneTileContent";
import { TwoByTwoTileContent } from "../TwoByTwoTileContent";
import { useRegisterTileUsage } from "../useRegisterTileUsage";
import { navigateToAddressBookApp } from "client_legacy/Apps/appNavigation";
import { AddressBookSettings } from "client/Api/Workspace/common";
import { AddressBookLiveTileContent } from "./AddressBookLiveTileContent";
import { TileRenderState } from "../../RenderState/common";
import { useBaseNavigation } from "../useInvokeNavigation";
import { TileContextProvider } from "../../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type AddressBookTileContentProps = {
	renderState: TileRenderState<AppType.AddressBook>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
	liveSettings: AddressBookSettings;
};

export const AddressBookTileContent = (props: AddressBookTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu,
		liveSettings
	} = props;

	const [navigationElement, navigateHandler] = useAddressBookTileNavigation(renderState, onEdit);

	const {
		dimension,
		data: { settings }
	} = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<AddressBookLiveTileContent
				liveUpdateInterval={liveSettings.liveUpdateInterval}
				configurableContactSources={settings.contactSources}
				defaultView={settings.defaultView}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		],
		[
			{ columnSpan: 2, rowSpan: 2 },
			() => (
				<TwoByTwoTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
AddressBookTileContent.displayName = "AddressBookTileContent";

export function useAddressBookTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.AddressBook>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useCallback(
			(): void => {
				registerTileUsage();
				navigateToAddressBookApp();
			},
			[registerTileUsage]
		)
	);
}
